import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { pressConferencesApi } from '../pressConferenceApi';
import { PRESS_CONFERENCE_CANCELLATION_REASON_MAX } from '../pressConferenceTypes';

/**
 * Calls a press conference off, with the reason.
 *
 * **A modal because the reason is required**, and because cancelling is the state most easily
 * confused with deleting — the body says which this is before the tick.
 */
export function CancelPressConferenceModal({
  conferenceId,
  referenceNumber,
  onClose,
  onCancelled,
}: {
  conferenceId: string;
  referenceNumber: string;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = reason.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await pressConferencesApi.cancel(conferenceId, reason.trim());
      onCancelled();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not cancel this press conference.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'cancel-press-conference' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Cancel ${referenceNumber}`}
      size="lg"
      id="cancel-press-conference-modal"
      /*
        NO Autogenerate. Why a conference was called off is a judgement somebody is making about a
        specific event — generating one would put words in their mouth.
      */
      confirm={{
        id: 'cancel-press-conference-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="cancel-press-conference-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="cancel-press-conference-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          CANCELLED IS NOT DELETED. Cancelled is an OUTCOME the register keeps; a delete is a
          correction to the register. Stated before the tick.
        */}
        <div className="flex flex-col gap-2 text-xs text-slate-600">
          <p>
            <strong>Cancelled is an outcome the register keeps.</strong> The press conference stays on the list and
            the calendar, marked cancelled, with this reason against it — and it can no longer be edited.
          </p>
          <p>
            If it was scheduled in error, or twice, <strong>Delete</strong> is the one you want instead.
          </p>
        </div>

        <Textarea
          id="cancel-press-conference-reason"
          label="Reason"
          required
          description="Why it was called off. It stays on the record."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={PRESS_CONFERENCE_CANCELLATION_REASON_MAX}
          error={reason.trim() ? error?.fieldError('reason') : 'Say why it is being cancelled.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
